/**
 * Appearance Settings Tab
 *
 * Manages visual preferences:
 * - High contrast mode
 * - Message display (compact/comfortable)
 * - Show avatars
 * - Link previews
 */

import { useState } from 'react';
import { useUserSettings, useUpdateSettings } from '@/hooks/useUserSettings';
import { useTheme } from '@/contexts/ThemeContext';

export const AppearanceSettings = () => {
  const { data: settings } = useUserSettings();
  const updateSettings = useUpdateSettings();
  const { theme, setTheme } = useTheme();

  const [highContrast, setHighContrast] = useState(false);
  const [messageDisplay, setMessageDisplay] = useState('comfortable');
  const [showAvatars, setShowAvatars] = useState(true);
  const [showLinkPreviews, setShowLinkPreviews] = useState(true);
  const [showLinkPreviewImages, setShowLinkPreviewImages] = useState(true);
  const [sidebarLayout, setSidebarLayout] = useState('stacked');
  const [pasteThreshold, setPasteThreshold] = useState(200);

  // Sync form state from fetched settings (adjust state during render on change)
  const [syncedSettings, setSyncedSettings] = useState(settings);
  if (settings && settings !== syncedSettings) {
    setSyncedSettings(settings);
    setHighContrast(settings.high_contrast_mode);
    setMessageDisplay(settings.message_display);
    setShowAvatars(settings.show_avatars);
    setShowLinkPreviews(settings.show_link_previews ?? true);
    setShowLinkPreviewImages(settings.show_link_preview_images ?? true);
    setPasteThreshold(settings.paste_as_file_threshold ?? 200);
    setSidebarLayout(settings.sidebar_layout ?? 'stacked');
  }

  const handleUpdate = async (field: string, value: string | number | boolean) => {
    try {
      await updateSettings.mutateAsync({ [field]: value });
    } catch (error) {
      console.error('Failed to update appearance settings:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Theme */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary mb-3">
          Тема
        </h3>
        <div className="space-y-2">
          {[
            { value: 'light', label: 'Светла', desc: 'Винаги светъл изглед' },
            { value: 'dark', label: 'Тъмна', desc: 'Винаги тъмен изглед' },
            { value: 'system', label: 'Системна', desc: 'Следвай настройката на устройството' },
          ].map((option) => (
            <label
              key={option.value}
              className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary"
            >
              <input
                type="radio"
                name="theme"
                value={option.value}
                checked={theme === option.value}
                onChange={(e) => setTheme(e.target.value as 'light' | 'dark' | 'system')}
                className="mt-1 w-4 h-4 text-blue-600"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">{option.label}</div>
                <div className="text-xs text-gray-500 dark:text-dark-text-secondary">{option.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Sidebar Layout */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary mb-3">
          Изглед на страничната лента
        </h3>
        <div className="space-y-2">
          {[
            { value: 'stacked', label: 'Подредени', desc: 'Всички секции видими, всяка може да се свие' },
            { value: 'tabbed', label: 'Табове', desc: 'Табове в горната част, само една секция видима' },
          ].map((option) => (
            <label
              key={option.value}
              className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary"
            >
              <input
                type="radio"
                name="sidebar-layout"
                value={option.value}
                checked={sidebarLayout === option.value}
                onChange={(e) => {
                  setSidebarLayout(e.target.value);
                  handleUpdate('sidebar_layout', e.target.value);
                }}
                className="mt-1 w-4 h-4 text-blue-600"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">{option.label}</div>
                <div className="text-xs text-gray-500 dark:text-dark-text-secondary">{option.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Message Display */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary mb-3">
          Message display
        </h3>
        <div className="space-y-2">
          {[
            { value: 'comfortable', label: 'Comfortable', desc: 'More spacing between messages' },
            { value: 'compact', label: 'Compact', desc: 'Fit more messages on screen' },
          ].map((option) => (
            <label
              key={option.value}
              className="flex items-start space-x-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary"
            >
              <input
                type="radio"
                name="message-display"
                value={option.value}
                checked={messageDisplay === option.value}
                onChange={(e) => {
                  setMessageDisplay(e.target.value);
                  handleUpdate('message_display', e.target.value);
                }}
                className="mt-1 w-4 h-4 text-blue-600"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">{option.label}</div>
                <div className="text-xs text-gray-500 dark:text-dark-text-secondary">{option.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Show Avatars */}
      <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-dark-border-primary rounded-lg">
        <div>
          <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">Show avatars</div>
          <div className="text-xs text-gray-500 dark:text-dark-text-secondary">Display user profile pictures</div>
        </div>
        <button
          onClick={() => {
            const newValue = !showAvatars;
            setShowAvatars(newValue);
            handleUpdate('show_avatars', newValue);
          }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            showAvatars ? 'bg-blue-600' : 'bg-gray-200 dark:bg-dark-bg-tertiary'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              showAvatars ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* High Contrast */}
      <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-dark-border-primary rounded-lg">
        <div>
          <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">High contrast mode</div>
          <div className="text-xs text-gray-500 dark:text-dark-text-secondary">Increase color contrast for accessibility</div>
        </div>
        <button
          onClick={() => {
            const newValue = !highContrast;
            setHighContrast(newValue);
            handleUpdate('high_contrast_mode', newValue);
          }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            highContrast ? 'bg-blue-600' : 'bg-gray-200'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              highContrast ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Link Preview Settings */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary mb-3">
          Link Previews
        </h3>

        <div className="space-y-3">
          {/* Show Link Previews Toggle */}
          <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-dark-border-primary rounded-lg">
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">Show link previews</div>
              <div className="text-xs text-gray-500 dark:text-dark-text-secondary">Display rich previews for links in messages</div>
            </div>
            <button
              onClick={() => {
                const newValue = !showLinkPreviews;
                setShowLinkPreviews(newValue);
                handleUpdate('show_link_previews', newValue);
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                showLinkPreviews ? 'bg-blue-600' : 'bg-gray-200 dark:bg-dark-bg-tertiary'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  showLinkPreviews ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Show Preview Images Toggle */}
          <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-dark-border-primary rounded-lg">
            <div>
              <div className={`text-sm font-medium ${
                !showLinkPreviews
                  ? 'text-gray-400 dark:text-dark-text-muted'
                  : 'text-gray-900 dark:text-dark-text-primary'
              }`}>
                Show preview images
              </div>
              <div className={`text-xs ${
                !showLinkPreviews
                  ? 'text-gray-400 dark:text-dark-text-muted'
                  : 'text-gray-500 dark:text-dark-text-secondary'
              }`}>
                Display images in link preview cards
              </div>
            </div>
            <button
              onClick={() => {
                const newValue = !showLinkPreviewImages;
                setShowLinkPreviewImages(newValue);
                handleUpdate('show_link_preview_images', newValue);
              }}
              disabled={!showLinkPreviews}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                showLinkPreviewImages && showLinkPreviews
                  ? 'bg-blue-600'
                  : 'bg-gray-200 dark:bg-dark-bg-tertiary'
              } ${!showLinkPreviews ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  showLinkPreviewImages ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Paste as File Threshold */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary mb-3">
          Голям текст като файл
        </h3>
        <div className="p-3 border border-gray-200 dark:border-dark-border-primary rounded-lg space-y-3">
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
              Праг за автоматично превръщане
            </div>
            <div className="text-xs text-gray-500 dark:text-dark-text-secondary">
              Поставен текст над този брой символи автоматично се превръща в .txt файл
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={50}
              max={5000}
              step={50}
              value={pasteThreshold}
              onChange={(e) => {
                const val = Number(e.target.value);
                setPasteThreshold(val);
              }}
              onMouseUp={() => handleUpdate('paste_as_file_threshold', pasteThreshold)}
              onTouchEnd={() => handleUpdate('paste_as_file_threshold', pasteThreshold)}
              className="flex-1 h-1.5 accent-blue-600 cursor-pointer"
            />
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                min={50}
                max={10000}
                step={50}
                value={pasteThreshold}
                onChange={(e) => {
                  const val = Math.max(50, Math.min(10000, Number(e.target.value) || 50));
                  setPasteThreshold(val);
                }}
                onBlur={() => handleUpdate('paste_as_file_threshold', pasteThreshold)}
                className="w-20 px-2 py-1 text-sm text-center rounded-md border border-gray-200 dark:border-dark-border-primary bg-white dark:bg-dark-bg-input text-gray-900 dark:text-dark-text-primary"
              />
              <span className="text-xs text-gray-500 dark:text-dark-text-secondary">символа</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppearanceSettings;
